import type { MouseEvent, ReactNode } from 'react'
import type { Customer, CustomerStatus, NextPageWithLayout, PlanId } from '@/types'

import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { downloadCsv, copyToClipboard } from '@/lib/csv'
import { TODAY, formatDate } from '@/lib/date'
import { Seo } from '@/components/generals/seo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { PageHeader } from '@/components/dashboard/page-header'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { EntityAvatar } from '@/components/dashboard/entity-avatar'
import { PlanBadge } from '@/components/dashboard/plan-badge'
import { CustomerStatusBadge } from '@/components/dashboard/status-badge'
import { EmptyState } from '@/components/dashboard/empty-state'
import { TablePagination } from '@/components/dashboard/table-pagination'
import { ConfirmDialog } from '@/components/dashboard/confirm-dialog'
import { HealthMeter } from '@/components/customers/health-meter'
import { CustomerActionsMenu } from '@/components/customers/customer-actions-menu'
import { AddCustomerDialog } from '@/components/customers/add-customer-dialog'
import { ChangePlanDialog } from '@/components/customers/change-plan-dialog'
import { CustomerSheet } from '@/components/customers/customer-sheet'
import { CUSTOMERS, getCustomerStats } from '@/data/customers'
import { PLANS, getPlan } from '@/data/plans'
import { CUSTOMER_STATUS_LABELS, CYCLE_LABELS, PROVINCES } from '@/constants/dashboard'

import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Download,
    Plus,
    Search,
    SearchX,
    X
} from 'lucide-react'

import {
    formatNumber,
    formatRupiah,
    formatRupiahCompact
} from '@/lib/format'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'

type SortKey = 'company' | 'mrr' | 'health' | 'joinedAt'

type SortState = {
    key: SortKey
    direction: 'asc' | 'desc'
}

type StatusFilter = 'all' | CustomerStatus

const STATUS_FILTERS: StatusFilter[] = ['all', 'active', 'trial', 'at-risk', 'canceled']

const PLAN_FILTER_ITEMS = [{ value: 'all', label: 'Semua paket' }, ...PLANS.map((plan) => ({ value: plan.id, label: plan.name }))]

const PROVINCE_FILTER_ITEMS = [{ value: 'all', label: 'Semua provinsi' }, ...PROVINCES.map((province) => ({ value: province, label: province }))]

const DEFAULT_SORT: SortState = { key: 'joinedAt', direction: 'desc' }

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const sortCustomers = (list: Customer[], sort: SortState): Customer[] => {
    const factor = sort.direction === 'asc' ? 1 : -1

    return [...list].sort((a, b) => {
        let result = 0

        if (sort.key === 'company') result = a.company.localeCompare(b.company, 'id')
        else if (sort.key === 'mrr') result = a.mrr - b.mrr
        else if (sort.key === 'health') result = a.health - b.health
        else result = compareText(a.joinedAt, b.joinedAt)

        return result * factor || compareText(a.id, b.id)
    })
}

interface SortableHeadProps {
    label: string
    sortKey: SortKey
    sort: SortState
    onSort: (key: SortKey) => void
    align?: 'left' | 'right'
    className?: string
}

const SortableHead = ({ label, sortKey, sort, onSort, align = 'left', className }: SortableHeadProps): ReactNode => {
    const active = sort.key === sortKey
    const Icon = !active ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown

    return (
        <TableHead
            aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
            className={cn(align === 'right' && 'text-right', className)}
        >
            <button
                type='button'
                onClick={() => onSort(sortKey)}
                className={cn(
                    '-mx-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-medium transition-colors outline-none hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50',
                    active ? 'text-foreground' : 'text-muted-foreground',
                    align === 'right' && 'flex-row-reverse'
                )}
            >
                {label}
                <Icon className='size-3.5' aria-hidden='true' />
            </button>
        </TableHead>
    )
}

const CustomersPage: NextPageWithLayout = (): ReactNode => {
    const router = useRouter()
    const [customers, setCustomers] = useState<Customer[]>(CUSTOMERS)
    const [query, setQuery] = useState('')
    const [status, setStatus] = useState<StatusFilter>('all')
    const [plan, setPlan] = useState<'all' | PlanId>('all')
    const [province, setProvince] = useState('all')
    const [sort, setSort] = useState<SortState>(DEFAULT_SORT)
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(10)
    const [selected, setSelected] = useState<Set<string>>(() => new Set())
    const [detailId, setDetailId] = useState<string | null>(null)
    const [addOpen, setAddOpen] = useState(false)
    const [planTarget, setPlanTarget] = useState<Customer | null>(null)
    const [cancelTarget, setCancelTarget] = useState<Customer | null>(null)

    // Deep links from search and notifications: ?q=<name> fills the search, ?tambah=1 opens the form
    useEffect(() => {
        if (!router.isReady) return

        const { q, tambah } = router.query
        if (typeof q !== 'string' && tambah !== '1') return

        if (typeof q === 'string') {
            setQuery(q)
            setStatus('all')
            setPlan('all')
            setProvince('all')
            setPage(1)
        }

        if (tambah === '1') setAddOpen(true)

        router.replace('/pelanggan', undefined, { shallow: true })
    }, [router, router.isReady, router.query])

    const term = query.trim().toLowerCase()

    // Every filter except status, so the status counts follow the search and dropdowns
    const baseFiltered = useMemo(() => customers.filter((customer) => (
        (plan === 'all' || customer.plan === plan)
        && (province === 'all' || customer.province === province)
        && (!term
            || customer.company.toLowerCase().includes(term)
            || customer.contact.toLowerCase().includes(term)
            || customer.email.toLowerCase().includes(term)
            || customer.id.toLowerCase().includes(term))
    )), [customers, plan, province, term])

    const statusCounts = useMemo(() => {
        const counts: Record<StatusFilter, number> = { all: baseFiltered.length, active: 0, trial: 0, 'at-risk': 0, canceled: 0 }
        baseFiltered.forEach((customer) => {
            counts[customer.status] += 1
        })
        return counts
    }, [baseFiltered])

    const filtered = useMemo(() => (status === 'all' ? baseFiltered : baseFiltered.filter((customer) => customer.status === status)), [baseFiltered, status])
    const sorted = useMemo(() => sortCustomers(filtered, sort), [filtered, sort])
    const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize))
    const currentPage = Math.min(page, pageCount)
    const visible = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize)

    const stats = useMemo(() => getCustomerStats(customers), [customers])
    const emails = useMemo(() => new Set(customers.map((customer) => customer.email.toLowerCase())), [customers])
    const nextId = useMemo(() => {
        const highest = customers.reduce((max, customer) => Math.max(max, Number(customer.id.replace('CUS-', '')) || 0), 0)
        return `CUS-${String(highest + 1).padStart(4, '0')}`
    }, [customers])

    const detailCustomer = detailId ? customers.find((customer) => customer.id === detailId) ?? null : null
    const filtersActive = term !== '' || status !== 'all' || plan !== 'all' || province !== 'all'

    const pageIds = visible.map((customer) => customer.id)
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
    const somePageSelected = pageIds.some((id) => selected.has(id))

    const resetFilters = (): void => {
        setQuery('')
        setStatus('all')
        setPlan('all')
        setProvince('all')
        setPage(1)
    }

    const onSort = (key: SortKey): void => {
        setSort((current) => (current.key === key
            ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
            : { key, direction: key === 'company' ? 'asc' : 'desc' }))
        setPage(1)
    }

    const toggleRow = (id: string, checked: boolean): void => {
        setSelected((current) => {
            const next = new Set(current)
            if (checked) next.add(id)
            else next.delete(id)
            return next
        })
    }

    const togglePage = (checked: boolean): void => {
        setSelected((current) => {
            const next = new Set(current)
            pageIds.forEach((id) => (checked ? next.add(id) : next.delete(id)))
            return next
        })
    }

    const updateCustomer = (id: string, changes: Partial<Customer>): void => {
        setCustomers((list) => list.map((customer) => (customer.id === id ? { ...customer, ...changes } : customer)))
    }

    const exportCustomers = (list: Customer[], label: string): void => {
        downloadCsv(
            `arunika-pelanggan-${TODAY}.csv`,
            ['ID', 'Perusahaan', 'Kontak', 'Email', 'Paket', 'Siklus', 'Status', 'MRR (Rp)', 'Pengguna', 'Kota', 'Provinsi', 'Bergabung', 'Skor kesehatan'],
            list.map((customer) => [
                customer.id,
                customer.company,
                customer.contact,
                customer.email,
                getPlan(customer.plan).name,
                CYCLE_LABELS[customer.cycle],
                CUSTOMER_STATUS_LABELS[customer.status],
                customer.mrr,
                customer.seats,
                customer.city,
                customer.province,
                customer.joinedAt,
                customer.health
            ])
        )

        toast.success(`${formatNumber(list.length)} pelanggan diekspor`, { description: label })
    }

    const copyEmail = async (customer: Customer): Promise<void> => {
        const copied = await copyToClipboard(customer.email)

        if (copied) toast.success('Email disalin', { description: customer.email })
        else toast.error('Email tidak bisa disalin', { description: 'Izinkan akses clipboard di browser Anda lalu coba lagi.' })
    }

    const onRowClick = (event: MouseEvent<HTMLTableRowElement>, id: string): void => {
        // Ignore clicks that started in a menu or dialog rendered outside the row
        if (!event.currentTarget.contains(event.target as Node)) return
        setDetailId(id)
    }

    const onCreate = (customer: Customer): void => {
        setCustomers((list) => [customer, ...list])
        resetFilters()
        setSort(DEFAULT_SORT)

        toast.success(`${customer.company} ditambahkan`, {
            description: customer.status === 'trial' ? 'Trial 14 hari dimulai hari ini.' : `Paket ${getPlan(customer.plan).name} aktif mulai hari ini.`,
            action: { label: 'Lihat', onClick: () => setDetailId(customer.id) }
        })
    }

    const statusOptions = STATUS_FILTERS.map((value) => ({
        value,
        label: (
            <>
                {value === 'all' ? 'Semua' : CUSTOMER_STATUS_LABELS[value]}
                <span className='text-xs text-muted-foreground tabular-nums'>
                    {formatNumber(statusCounts[value])}
                </span>
            </>
        )
    }))

    return (
        <>
            <Seo title='Pelanggan' description='Kelola akun pelanggan, paket, dan status langganan di Arunika.' />

            <div className='space-y-6'>
                <PageHeader
                    title='Pelanggan'
                    description='Semua akun yang pernah mencoba atau berlangganan Arunika, lengkap dengan paket dan kesehatan akunnya.'
                    actions={(
                        <>
                            <Button variant='outline' size='lg' onClick={() => exportCustomers(sorted, filtersActive ? 'Sesuai filter yang aktif.' : 'Seluruh data pelanggan.')}>
                                <Download data-icon='inline-start' />
                                Ekspor CSV
                            </Button>
                            <Button size='lg' onClick={() => setAddOpen(true)}>
                                <Plus data-icon='inline-start' />
                                Tambah pelanggan
                            </Button>
                        </>
                    )}
                />

                <dl className='grid grid-cols-2 gap-3 lg:grid-cols-4'>
                    {[
                        { label: 'Pelanggan berbayar', value: formatNumber(stats.paying) },
                        { label: 'MRR dari pelanggan', value: formatRupiahCompact(stats.mrr) },
                        { label: 'Trial berjalan', value: formatNumber(stats.trial) },
                        { label: 'Perlu perhatian', value: formatNumber(stats.atRisk) }
                    ].map((item) => (
                        <div key={item.label} className='rounded-xl border bg-card px-4 py-3.5'>
                            <dt className='text-xs text-muted-foreground'>
                                {item.label}
                            </dt>
                            <dd className='mt-1 font-heading text-xl font-semibold text-foreground tabular-nums'>
                                {item.value}
                            </dd>
                        </div>
                    ))}
                </dl>

                <section aria-label='Daftar pelanggan' className='rounded-xl border bg-card shadow-[0_1px_2px_0_rgb(28_25_23/0.04)]'>
                    <div className='space-y-4 border-b p-4 sm:p-5'>
                        <div className='no-scrollbar -mx-1 overflow-x-auto px-1'>
                            <SegmentedControl
                                label='Filter status pelanggan'
                                value={status}
                                onChange={(value) => {
                                    setStatus(value)
                                    setPage(1)
                                }}
                                options={statusOptions}
                            />
                        </div>

                        <div className='flex flex-col gap-3 md:flex-row md:items-center'>
                            <div className='relative md:max-w-sm md:flex-1'>
                                <Search className='pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground' aria-hidden='true' />
                                <Input
                                    type='search'
                                    value={query}
                                    onChange={(event) => {
                                        setQuery(event.target.value)
                                        setPage(1)
                                    }}
                                    placeholder='Cari perusahaan, kontak, email, atau ID'
                                    aria-label='Cari pelanggan'
                                    className='h-9 pl-8'
                                />
                            </div>

                            <div className='flex flex-wrap items-center gap-2'>
                                <Select
                                    items={PLAN_FILTER_ITEMS}
                                    value={plan}
                                    onValueChange={(value) => {
                                        if (!value) return
                                        setPlan(value as 'all' | PlanId)
                                        setPage(1)
                                    }}
                                >
                                    <SelectTrigger className='h-9! w-40' aria-label='Filter paket'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PLAN_FILTER_ITEMS.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                <Select
                                    items={PROVINCE_FILTER_ITEMS}
                                    value={province}
                                    onValueChange={(value) => {
                                        if (!value) return
                                        setProvince(value as string)
                                        setPage(1)
                                    }}
                                >
                                    <SelectTrigger className='h-9! w-48' aria-label='Filter provinsi'>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PROVINCE_FILTER_ITEMS.map((item) => (
                                            <SelectItem key={item.value} value={item.value}>
                                                {item.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>

                                {filtersActive && (
                                    <Button variant='ghost' onClick={resetFilters} className='h-9 text-muted-foreground'>
                                        <X data-icon='inline-start' />
                                        Reset filter
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>

                    {selected.size > 0 && (
                        <div className='flex flex-wrap items-center gap-3 border-b bg-muted/50 px-4 py-2.5 text-sm sm:px-5'>
                            <span className='font-medium text-foreground'>
                                {formatNumber(selected.size)} pelanggan dipilih
                            </span>
                            <Button
                                variant='outline'
                                size='sm'
                                onClick={() => exportCustomers(customers.filter((customer) => selected.has(customer.id)), 'Pelanggan yang Anda pilih.')}
                            >
                                <Download data-icon='inline-start' />
                                Ekspor terpilih
                            </Button>
                            <Button variant='ghost' size='sm' onClick={() => setSelected(new Set())}>
                                Batal pilih
                            </Button>
                        </div>
                    )}

                    {visible.length === 0
                        ? (
                            <EmptyState
                                icon={SearchX}
                                title='Tidak ada pelanggan yang cocok'
                                description='Coba kata kunci lain atau longgarkan filter paket, status, dan provinsi.'
                                action={(
                                    <Button variant='outline' onClick={resetFilters}>
                                        Reset filter
                                    </Button>
                                )}
                            />
                        )
                        : (
                            <Table>
                                <TableHeader>
                                    <TableRow className='bg-muted/40 hover:bg-muted/40'>
                                        <TableHead className='w-12 pl-4 sm:pl-5'>
                                            <Checkbox
                                                checked={allPageSelected}
                                                indeterminate={!allPageSelected && somePageSelected}
                                                onCheckedChange={(checked) => togglePage(checked === true)}
                                                aria-label='Pilih semua pelanggan di halaman ini'
                                            />
                                        </TableHead>
                                        <SortableHead label='Pelanggan' sortKey='company' sort={sort} onSort={onSort} />
                                        <TableHead className='hidden text-xs font-medium text-muted-foreground md:table-cell'>Paket</TableHead>
                                        <TableHead className='text-xs font-medium text-muted-foreground'>Status</TableHead>
                                        <SortableHead label='MRR' sortKey='mrr' sort={sort} onSort={onSort} align='right' />
                                        <TableHead className='hidden text-right text-xs font-medium text-muted-foreground lg:table-cell'>Pengguna</TableHead>
                                        <SortableHead label='Kesehatan' sortKey='health' sort={sort} onSort={onSort} className='hidden lg:table-cell' />
                                        <SortableHead label='Bergabung' sortKey='joinedAt' sort={sort} onSort={onSort} className='hidden md:table-cell' />
                                        <TableHead className='w-12 pr-4 sm:pr-5'>
                                            <span className='sr-only'>Aksi</span>
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>

                                <TableBody>
                                    {visible.map((customer) => {
                                        const isSelected = selected.has(customer.id)
                                        const paying = customer.status === 'active' || customer.status === 'at-risk'

                                        return (
                                            <TableRow
                                                key={customer.id}
                                                data-state={isSelected ? 'selected' : undefined}
                                                className='cursor-pointer'
                                                onClick={(event) => onRowClick(event, customer.id)}
                                            >
                                                <TableCell className='pl-4 sm:pl-5' onClick={(event) => event.stopPropagation()}>
                                                    <Checkbox
                                                        checked={isSelected}
                                                        onCheckedChange={(checked) => toggleRow(customer.id, checked === true)}
                                                        aria-label={`Pilih ${customer.company}`}
                                                    />
                                                </TableCell>

                                                <TableCell className='max-w-[300px] py-3'>
                                                    <div className='flex min-w-0 items-center gap-3'>
                                                        <EntityAvatar name={customer.company} size='sm' />
                                                        <div className='min-w-0'>
                                                            <button
                                                                type='button'
                                                                onClick={(event) => {
                                                                    event.stopPropagation()
                                                                    setDetailId(customer.id)
                                                                }}
                                                                className='block max-w-full truncate rounded-sm text-left font-medium text-foreground outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50'
                                                            >
                                                                {customer.company}
                                                            </button>
                                                            <p className='truncate text-xs text-muted-foreground'>
                                                                {customer.email}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </TableCell>

                                                <TableCell className='hidden md:table-cell'>
                                                    <div className='flex flex-col items-start gap-1'>
                                                        <PlanBadge plan={customer.plan} />
                                                        <span className='text-[11px] text-muted-foreground'>
                                                            {CYCLE_LABELS[customer.cycle]}
                                                        </span>
                                                    </div>
                                                </TableCell>

                                                <TableCell>
                                                    <CustomerStatusBadge status={customer.status} />
                                                </TableCell>

                                                <TableCell className='text-right'>
                                                    {paying
                                                        ? (
                                                            <span className='font-medium text-foreground tabular-nums'>
                                                                {formatRupiah(customer.mrr)}
                                                            </span>
                                                        )
                                                        : (
                                                            <span className='text-xs text-muted-foreground'>
                                                                {customer.status === 'trial' && customer.trialEndsAt
                                                                    ? `Trial s.d. ${formatDate(customer.trialEndsAt)}`
                                                                    : 'Tidak ditagih'}
                                                            </span>
                                                        )}
                                                </TableCell>

                                                <TableCell className='hidden text-right text-muted-foreground tabular-nums lg:table-cell'>
                                                    {formatNumber(customer.seats)}
                                                </TableCell>

                                                <TableCell className='hidden lg:table-cell'>
                                                    <HealthMeter score={customer.health} />
                                                </TableCell>

                                                <TableCell className='hidden text-muted-foreground md:table-cell'>
                                                    {formatDate(customer.joinedAt)}
                                                </TableCell>

                                                <TableCell className='pr-4 text-right sm:pr-5' onClick={(event) => event.stopPropagation()}>
                                                    <CustomerActionsMenu
                                                        customer={customer}
                                                        onView={(item) => setDetailId(item.id)}
                                                        onCopyEmail={copyEmail}
                                                        onChangePlan={setPlanTarget}
                                                        onCancel={setCancelTarget}
                                                        onReactivate={(item) => {
                                                            updateCustomer(item.id, { status: 'active', canceledAt: null, health: Math.max(item.health, 60) })
                                                            toast.success(`${item.company} aktif kembali`)
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                                </TableBody>
                            </Table>
                        )}

                    <div className='border-t px-4 py-3 sm:px-5'>
                        <TablePagination
                            page={currentPage}
                            pageSize={pageSize}
                            total={sorted.length}
                            noun='pelanggan'
                            onPageChange={setPage}
                            onPageSizeChange={(size) => {
                                setPageSize(size)
                                setPage(1)
                            }}
                        />
                    </div>
                </section>
            </div>

            <AddCustomerDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                nextId={nextId}
                isEmailTaken={(email) => emails.has(email)}
                onCreate={onCreate}
            />

            <CustomerSheet
                customer={detailCustomer}
                onOpenChange={(open) => {
                    if (!open) setDetailId(null)
                }}
                onChangePlan={setPlanTarget}
                onCancel={setCancelTarget}
                onReactivate={(item) => {
                    updateCustomer(item.id, { status: 'active', canceledAt: null, health: Math.max(item.health, 60) })
                    toast.success(`${item.company} aktif kembali`)
                }}
            />

            <ChangePlanDialog
                customer={planTarget}
                onOpenChange={(open) => {
                    if (!open) setPlanTarget(null)
                }}
                onSave={(customer, changes) => {
                    updateCustomer(customer.id, changes)
                    toast.success('Paket diperbarui', {
                        description: `${customer.company} sekarang memakai paket ${getPlan(changes.plan).name}, ${formatRupiah(changes.mrr)} per bulan.`
                    })
                }}
            />

            <ConfirmDialog
                open={cancelTarget !== null}
                onOpenChange={(open) => {
                    if (!open) setCancelTarget(null)
                }}
                title='Batalkan langganan?'
                description={cancelTarget
                    ? `${cancelTarget.company} akan kehilangan akses di akhir periode tagihan dan MRR berkurang ${formatRupiah(cancelTarget.status === 'trial' ? 0 : cancelTarget.mrr)}.`
                    : ''}
                confirmLabel='Ya, batalkan'
                destructive
                onConfirm={() => {
                    if (!cancelTarget) return
                    updateCustomer(cancelTarget.id, { status: 'canceled', canceledAt: TODAY })
                    toast.success(`Langganan ${cancelTarget.company} dibatalkan`)
                }}
            />
        </>
    )
}

export default CustomersPage
